import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, AlertTriangle, CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import { getToken } from '@/utils/tokenManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL_MS = 60_000;

// Overall verdict style mappings — drives the big top-of-card banner.
const VERDICT_STYLES = {
  ok: { label: 'All systems operational', badge: 'bg-green-500', icon: CheckCircle, iconColor: 'text-green-500', border: 'border-green-500/40 bg-green-900/10' },
  warn: { label: 'Degraded', badge: 'bg-yellow-500', icon: AlertTriangle, iconColor: 'text-yellow-500', border: 'border-yellow-500/40 bg-yellow-900/10' },
  down: { label: 'Action needed', badge: 'bg-red-500', icon: XCircle, iconColor: 'text-red-500', border: 'border-red-500/40 bg-red-900/10' },
};

// Per-check row colour + icon mapping.
const ROW_STYLES = {
  ok: { dot: 'bg-green-500', icon: CheckCircle, iconColor: 'text-green-400', label: 'OK', badge: 'bg-green-600' },
  warn: { dot: 'bg-yellow-500', icon: AlertTriangle, iconColor: 'text-yellow-400', label: 'Warning', badge: 'bg-yellow-600' },
  down: { dot: 'bg-red-500', icon: XCircle, iconColor: 'text-red-400', label: 'Down', badge: 'bg-red-600' },
};

const HealthCheckPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const runOnce = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/health-check`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (cancelled) return;
        if (!res.ok) throw new Error(`Request failed (HTTP ${res.status})`);
        const json = await res.json();
        if (cancelled) return;
        setData(json);
        setError(null);
        setLastUpdatedAt(new Date());
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Could not reach the health check endpoint.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    runOnce();
    const interval = setInterval(runOnce, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [refreshTrigger]);

  const triggerRefresh = useCallback(() => {
    setLoading(true);
    setRefreshTrigger((n) => n + 1);
  }, []);

  if (loading && !data) {
    return (
      <Card className="border border-gray-700 bg-gray-900" data-testid="health-check-panel-loading">
        <CardContent className="p-6 flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
          <span className="text-gray-300 text-sm">Checking live status…</span>
        </CardContent>
      </Card>
    );
  }

  const verdict = data?.status || (error ? 'down' : 'ok');
  const verdictStyle = VERDICT_STYLES[verdict] || VERDICT_STYLES.ok;
  const VerdictIcon = verdictStyle.icon;

  return (
    <Card className={`border-2 ${verdictStyle.border}`} data-testid="health-check-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 text-white">
            <Activity className="w-5 h-5 text-emerald-500" />
            <span className="text-base sm:text-lg">Health Check</span>
            <span className="text-xs text-gray-500 hidden sm:inline">· live status</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${verdictStyle.badge} text-white text-xs uppercase tracking-wide`} data-testid="health-check-verdict">
              {verdictStyle.label}
            </Badge>
            <Button
              onClick={triggerRefresh}
              variant="outline"
              size="sm"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
              data-testid="refresh-health-check"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
          <VerdictIcon className={`w-4 h-4 ${verdictStyle.iconColor}`} />
          <span>
            {error
              ? `Couldn't refresh: ${error}`
              : `Auto-refreshes every 60s · ${lastUpdatedAt ? `last checked ${lastUpdatedAt.toLocaleTimeString()}` : 'updating…'}`}
          </span>
        </div>
        <div className="space-y-2" data-testid="health-check-rows">
          {(data?.checks || []).map((check) => {
            const style = ROW_STYLES[check.status] || ROW_STYLES.warn;
            const RowIcon = style.icon;
            return (
              <div
                key={check.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-gray-900/60 border border-gray-800"
                data-testid={`health-check-row-${check.id}`}
              >
                <RowIcon className={`w-5 h-5 mt-0.5 ${style.iconColor}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-white truncate">{check.name}</p>
                    <Badge className={`${style.badge} text-white text-[10px] uppercase tracking-wide`}>
                      {style.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{check.detail}</p>
                </div>
              </div>
            );
          })}
          {(!data?.checks || data.checks.length === 0) && !loading && (
            <p className="text-xs text-gray-500 italic">No checks reported.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HealthCheckPanel;
